'use client';

import { useAuth } from '@/backend/context/auth-management';
import { apiClient } from '@/backend/api/api-client';
import type { Product, ProductWithId, ProductInput } from '@/shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProductService } from '../services/product-service';
import { APP_KEYS } from './query-keys';

const upsertProductList = (
  current: ProductWithId[] | undefined,
  next: ProductWithId,
) => {
  const list = Array.isArray(current) ? [...current] : [];
  const index = list.findIndex((item) => item.id === next.id);
  if (index >= 0) {
    list[index] = { ...list[index], ...next };
    return list;
  }
  return [next, ...list];
};

const removeProductFromList = (
  current: ProductWithId[] | undefined,
  productId: string,
) => (Array.isArray(current) ? current.filter((item) => item.id !== productId) : []);

const patchProductList = (
  current: ProductWithId[] | undefined,
  productId: string,
  data: Partial<Product>,
) => {
  const list = Array.isArray(current) ? [...current] : [];
  const index = list.findIndex((item) => item.id === productId);
  if (index < 0) return list;
  list[index] = {
    ...list[index],
    ...data,
  } as ProductWithId;
  return list;
};

export const useProducts = {
  /**
   * Obtiene todos los productos del dueño actual
   */
  getAll: () => {
    const ownerId = useAuth((s) => s.user?.uid);

    return useQuery<ProductWithId[]>({
      queryKey: APP_KEYS.products.list(ownerId || 'no-owner'),
      queryFn: () => {
        if (!ownerId) return [];
        return ProductService.getProducts(ownerId);
      },
      enabled: !!ownerId,
    });
  },

  /**
   * Obtiene un producto individual
   */
  getOne: (productId: string | undefined) => {
    const queryClient = useQueryClient();
    const ownerId = useAuth((s) => s.user?.uid);

    return useQuery<ProductWithId | null>({
      queryKey: APP_KEYS.products.detail(
        ownerId || 'no-owner',
        productId || 'no-id',
      ),
      queryFn: async () => {
        if (!ownerId || !productId) return null;
        // El servicio actual no tiene getById, así que usamos getAll y filtramos
        // TODO: Implementar getById en ProductService para eficiencia
        const all = await ProductService.getProducts(ownerId);
        return all.find((p) => p.id === productId) || null;
      },
      enabled: !!productId && !!ownerId,

      initialData: () => {
        if (!ownerId) return undefined;
        return queryClient
          .getQueryData<ProductWithId[]>(APP_KEYS.products.list(ownerId))
          ?.find((p) => p.id === productId);
      },
    });
  },

  create: () => {
    const queryClient = useQueryClient();
    const ownerId = useAuth((s) => s.user?.uid);

    return useMutation({
      mutationFn: (data: ProductInput) => {
        if (!ownerId) throw new Error('No hay sesión activa');
        return ProductService.create(ownerId, data);
      },
      onSuccess: (newProduct, variables) => {
        queryClient.setQueryData<ProductWithId[]>(
          APP_KEYS.products.list(ownerId!),
          (current) => upsertProductList(current, { ...variables, id: newProduct } as ProductWithId),
        );
      },
    });
  },

  update: () => {
    const queryClient = useQueryClient();
    const ownerId = useAuth((s) => s.user?.uid);
    const user = useAuth((s) => s.user);
    const slug = useAuth((s) => s.profile?.public?.branding?.slug);

    return useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: Partial<Product>;
      }) => {
        if (!ownerId) throw new Error('No hay sesión activa');
        await ProductService.update(ownerId, id, data);
      },
      onSuccess: async (_, variables) => {
        if (!ownerId) return;

        queryClient.setQueryData<ProductWithId[]>(
          APP_KEYS.products.list(ownerId),
          (current) => patchProductList(current, variables.id, variables.data),
        );

        queryClient.setQueryData<ProductWithId | null>(
          APP_KEYS.products.detail(ownerId, variables.id),
          (current) => (current ? ({ ...current, ...variables.data } as ProductWithId) : current),
        );

        queryClient.invalidateQueries({
          queryKey: APP_KEYS.products.detail(ownerId, variables.id),
        });

        queryClient.invalidateQueries({
          queryKey: APP_KEYS.products.list(ownerId),
        });

        // Revalidate Next.js cache if applicable
        if (slug && user) {
          apiClient.post('/api/revalidate', { slug, ownerId })
            .catch((e) => console.error('[useProduct] Failed to ping revalidate API:', e));
        }
      },
    });
  },

  delete: () => {
    const queryClient = useQueryClient();
    const ownerId = useAuth((s) => s.user?.uid);

    return useMutation({
      mutationFn: (productId: string) => {
        if (!ownerId) throw new Error('No hay sesión activa');
        return ProductService.delete(ownerId, productId);
      },
      onSuccess: (_, productId) => {
        if (!ownerId) return;

        const removed = queryClient
          .getQueryData<ProductWithId[]>(APP_KEYS.products.list(ownerId))
          ?.find((item) => item.id === productId);

        queryClient.setQueryData<ProductWithId[]>(
          APP_KEYS.products.list(ownerId),
          (current) => removeProductFromList(current, productId),
        );

        if (removed) {
          queryClient.setQueryData<ProductWithId[]>(
            [...APP_KEYS.products.owner(ownerId), 'deleted'],
            (current) => upsertProductList(current, {
              ...removed,
              audit: {
                ...(removed as any).audit,
                deletedAt: (removed as any).audit?.deletedAt || new Date().toISOString(),
              },
            } as ProductWithId),
          );
        }

        queryClient.removeQueries({
          queryKey: APP_KEYS.products.detail(ownerId, productId),
        });

        queryClient.invalidateQueries({
          queryKey: APP_KEYS.products.list(ownerId),
        });
      },
    });
  },

  /**
   * Obtiene todos los productos eliminados
   */
  getDeleted: () => {
    const ownerId = useAuth((s) => s.user?.uid);

    return useQuery<ProductWithId[]>({
      queryKey: [...APP_KEYS.products.owner(ownerId || 'no-owner'), 'deleted'],
      queryFn: () => {
        if (!ownerId) return [];
        return ProductService.getDeleted(ownerId);
      },
      enabled: !!ownerId,
    });
  },

  /**
   * Restaura un producto eliminado
   */
  restore: () => {
    const queryClient = useQueryClient();
    const ownerId = useAuth((s) => s.user?.uid);

    return useMutation({
      mutationFn: (productId: string) => {
        if (!ownerId) throw new Error('No hay sesión activa');
        return ProductService.restore(ownerId, productId);
      },
      onSuccess: (_, productId) => {
        if (!ownerId) return;

        const restored = queryClient
          .getQueryData<ProductWithId[]>([...APP_KEYS.products.owner(ownerId), 'deleted'])
          ?.find((item) => item.id === productId);

        if (restored) {
          queryClient.setQueryData<ProductWithId[]>(
            APP_KEYS.products.list(ownerId),
            (current) => upsertProductList(current, {
              ...restored,
              audit: {
                ...(restored as any).audit,
                deletedAt: null,
              },
            } as ProductWithId),
          );
        }

        queryClient.setQueryData<ProductWithId[]>(
          [...APP_KEYS.products.owner(ownerId), 'deleted'],
          (current) => removeProductFromList(current, productId),
        );

        queryClient.invalidateQueries({
          queryKey: APP_KEYS.products.list(ownerId),
        });

        queryClient.invalidateQueries({
          queryKey: [...APP_KEYS.products.owner(ownerId), 'deleted'],
        });
      },
    });
  },
};
